import { useState } from "react";
import { ButtonsRight } from "../../../../interfaces";
import { useHighlight } from "../../../../context";
import { HighlightCard } from "./NoteCard";

const COLORS = ["#F9CE4E", "#999FD9", "#E0314E"] as const;

interface Props {
  openButtons: ButtonsRight;
  setOpenButtons: (button: ButtonsRight) => void;
}

export const NotesComponent = ({ openButtons, setOpenButtons }: Props) => {
  const { highlights, updateHighlight, removeHighlight, loading } =
    useHighlight();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedHighlightId, setSelectedHighlightId] = useState<string | null>(
    null
  );
  const [tempNote, setTempNote] = useState("");
  const [tempColor, setTempColor] = useState<string | null>(null);

  const selectedHighlight = highlights.find(
    (h) => h.id === selectedHighlightId
  );

  const handleEdit = (id: string) => {
    const hl = highlights.find((h) => h.id === id);
    if (hl) {
      setSelectedHighlightId(id);
      setTempNote(hl.note || "");
      setTempColor(hl.color);
      setIsEditing(true);
    }
  };

  const handleDelete = (id: string) => {
    removeHighlight(id);
  };

  const handleSave = () => {
    if (!selectedHighlightId) return;
    updateHighlight(selectedHighlightId, {
      note: tempNote,
      color: tempColor || COLORS[0],
    });
    setIsEditing(false);
    setSelectedHighlightId(null);
  };

  // Función para copiar texto al portapapeles
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div
      id="option-3"
      className={`aside__content ${openButtons.optNotes ? "active" : ""}`}
    >
      <div className="aside__content-header">
        <h3>Notas</h3>
        <button
          className="btn btn-rounded btn-rounded-transparent btn-close"
          aria-label="Cerrar panel de notas"
          onClick={() => {
            setOpenButtons({ ...openButtons, optNotes: false });
            setIsEditing(false);
          }}
        >
          <span className="btn__icon">
            <i className="icon icon-vpro-close"></i>
          </span>
        </button>
      </div>

      <div className="aside__content-data main-sb" style={{ height: "100%" }}>
        {loading ? (
          <p style={{ padding: "1rem" }}>Cargando notas...</p>
        ) : !isEditing ? (
          <div className="card-wrapper">
            <div id="card-grp-1" className="card-grp">
              {highlights.length > 0 ? (
                highlights.map((highlight) => (
                  <HighlightCard
                    key={highlight.id}
                    highlight={highlight}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))
              ) : (
                <p style={{ padding: "1rem", textAlign: "center" }}>
                  No hay notas ni resaltados aún.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`sel-win-edit active`}
            style={{
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 9999,
            }}
          >
            <div className="sel-win-edit__header">
              <div className="sel-win-edit__header-options">
                <button
                  type="button"
                  className="btn btn-alt-primary btn-opts-b selected tippy"
                  data-tippy-content="Editar color de resaltado"
                  aria-label="Editar color de resaltado y editar nota"
                >
                  <span className="btn__icon">
                    <i className="icon icon-highlighter"></i>
                  </span>
                </button>
              </div>
              <button
                className="btn btn-rounded btn-rounded-transparent cancel-btn"
                aria-label="Cerrar panel de opciones de selección"
                onClick={() => setIsEditing(false)}
              >
                <span className="btn__icon">
                  <i className="icon icon-vpro-close"></i>
                </span>
              </button>
            </div>

            <div className="sel-win-edit__body main-sb">
              <div id="pill-0b" className="sel-win-edit__body-content-b active">
                <div className="sel-win-edit__content">
                  <div className="sel-win-edit__content-color-picker">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`btn btn-rounded btn-rounded--border tippy ${
                          tempColor === c ? "selected" : ""
                        }`}
                        style={{
                          backgroundColor: c,
                          border:
                            tempColor === c
                              ? "2px solid #000"
                              : "1px solid #ccc",
                        }}
                        onClick={() => setTempColor(c)}
                        aria-label={`Resaltar texto en color ${c}`}
                      ></button>
                    ))}
                  </div>

                  <div className="sel-win-edit__content-selection">
                    <div className="sel-win-edit__content-selection-body main-sb">
                      <a
                        href="#"
                        className="selection selection-color-yellow"
                        style={{ backgroundColor: tempColor || "transparent" }}
                      >
                        {selectedHighlight?.text}
                      </a>
                      {/* Botón de copia para el texto resaltado */}
                      <button
                        type="button"
                        className="btn btn-rounded btn-rounded-secondary"
                        onClick={() =>
                          copyToClipboard(selectedHighlight?.text || "")
                        }
                        style={{ marginTop: "12px" }}
                        aria-label="Copiar texto resaltado"
                      >
                        <span className="btn__icon">
                          <i className="icon icon-copy"></i>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="sel-win-edit__content-comment">
                    <div className="sel-win-edit__txt-field">
                      <textarea
                        name="Nota"
                        id="txtArea2"
                        className="txt-area"
                        cols={30}
                        rows={10}
                        placeholder="Escribe una nota"
                        value={tempNote}
                        onChange={(e) => setTempNote(e.target.value)}
                      ></textarea>
                      {/* Botón de copia para la nota - dentro de la caja de texto */}
                      <button
                        type="button"
                        className="btn btn-rounded btn-rounded-secondary"
                        onClick={() => copyToClipboard(tempNote)}
                        aria-label="Copiar nota"
                      >
                        <span className="btn__icon">
                          <i className="icon icon-copy"></i>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <p>
                  Modificado el{" "}
                  {selectedHighlight?.fecha ||
                    new Date().toLocaleDateString("es-CO")}{" "}
                  en {selectedHighlight?.chapterId}
                </p>
              </div>
            </div>

            <div className="sel-win-edit__footer">
              <button
                type="button"
                className="btn btn-alt-secondary cancel-btn"
                aria-label="Cancelar"
                onClick={() => setIsEditing(false)}
              >
                <span className="btn__label">Cancelar</span>
              </button>
              <button
                type="button"
                className="btn btn-alt-primary btn-alt-primary--red"
                onClick={handleSave}
              >
                <span className="btn__label">Guardar</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
